import { Modal } from '../common/Modal'
import { Button } from '../common/Button'

const StatusBadge = ({ status }) => {
  const s = status || 'Active'
  const styles = {
    'Active': 'bg-green-100 text-green-800',
    'Inactive': 'bg-red-100 text-red-800',
    'On Leave': 'bg-yellow-100 text-yellow-800',
    'Resigned': 'bg-orange-100 text-orange-800',
    'Terminated': 'bg-gray-100 text-gray-800'
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[s] || 'bg-gray-100 text-gray-800'}`}>
      {s}
    </span>
  )
}

const InfoField = ({ label, value, isEditing, children, mono = false }) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-slate-600">{label}</label>
    {isEditing ? children : (
      <p className={`text-slate-900 ${mono ? 'font-mono bg-slate-100 px-3 py-2 rounded-lg' : ''}`}>
        {value || 'N/A'}
      </p>
    )}
  </div>
)

const EmployeeDetailsModal = ({
  isOpen,
  onClose,
  employee,
  isEditing,
  editFormData,
  onInputChange,
  uniqueDepartments,
  uniqueLocations,
  formatExperience,
  isDeleting,
  hasPermission,
  onEditNavigation
}) => {
  if (!employee) return null

  const fullName = `${employee.first_name || employee.firstName || ''} ${employee.last_name || employee.lastName || ''}`.trim()
  const employeeId = employee.employee_id || employee.employeeId || employee.id

  const renderContent = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
      {/* Personal Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">Personal Information</h3>

        <InfoField label="Employee ID" value={employeeId} mono />

        <InfoField label="First Name" value={employee.first_name || employee.firstName} isEditing={isEditing}>
          <input
            type="text"
            value={editFormData.firstName}
            onChange={(e) => onInputChange('firstName', e.target.value)}
            className="input-field w-full"
          />
        </InfoField>

        <InfoField label="Last Name" value={employee.last_name || employee.lastName} isEditing={isEditing}>
          <input
            type="text"
            value={editFormData.lastName}
            onChange={(e) => onInputChange('lastName', e.target.value)}
            className="input-field w-full"
          />
        </InfoField>

        <InfoField label="Email" value={employee.email} isEditing={isEditing}>
          <input
            type="email"
            value={editFormData.email}
            onChange={(e) => onInputChange('email', e.target.value)}
            className="input-field w-full"
          />
        </InfoField>

        <InfoField label="Phone" value={employee.phone || employee.mobile_number} isEditing={isEditing}>
          <input
            type="tel"
            value={editFormData.phone}
            onChange={(e) => onInputChange('phone', e.target.value)}
            className="input-field w-full"
          />
        </InfoField>

        <InfoField label="Date of Birth" value={employee.dateOfBirth || employee.date_of_birth} isEditing={isEditing}>
          <input
            type="date"
            value={editFormData.dateOfBirth}
            onChange={(e) => onInputChange('dateOfBirth', e.target.value)}
            className="input-field w-full"
          />
        </InfoField>
      </div>

      {/* Job Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">Job Information</h3>

        <InfoField label="Role" value={employee.designation || employee.title} isEditing={isEditing}>
          <input
            type="text"
            value={editFormData.title}
            onChange={(e) => onInputChange('title', e.target.value)}
            className="input-field w-full"
          />
        </InfoField>

        <InfoField label="Department" value={employee.department} isEditing={isEditing}>
          <select
            value={editFormData.department}
            onChange={(e) => onInputChange('department', e.target.value)}
            className="input-field w-full"
          >
            <option value="">Select Department</option>
            {uniqueDepartments.map(dept => <option key={dept} value={dept}>{dept}</option>)}
          </select>
        </InfoField>

        <InfoField label="Location" value={employee.city || employee.location} isEditing={isEditing}>
          <select
            value={editFormData.location}
            onChange={(e) => onInputChange('location', e.target.value)}
            className="input-field w-full"
          >
            <option value="">Select Location</option>
            {uniqueLocations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
          </select>
        </InfoField>

        <InfoField label="Employment Type" value={employee.employmentType || employee.employment_type} isEditing={isEditing}>
          <select
            value={editFormData.employmentType || ''}
            onChange={(e) => onInputChange('employmentType', e.target.value)}
            className="input-field w-full"
          >
            <option value="">Select employment type</option>
            {['Full-time', 'Part-time', 'Contract', 'Permanent', 'Intern'].map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </InfoField>

        <InfoField label="Hire Date" value={employee.hireDate || employee.date_of_joining} isEditing={isEditing}>
          <input
            type="date"
            value={editFormData.hireDate}
            onChange={(e) => onInputChange('hireDate', e.target.value)}
            className="input-field w-full"
          />
        </InfoField>

        <InfoField label="Status" value={<StatusBadge status={employee.employee_status} />} isEditing={isEditing}>
          <select
            value={editFormData.employeeStatus || 'Active'}
            onChange={(e) => onInputChange('employeeStatus', e.target.value)}
            className="input-field w-full"
          >
            {['Active', 'Inactive', 'Resigned', 'Terminated'].map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </InfoField>

        <InfoField label="Experience" value={formatExperience(employee.total_experience || employee.experience)} />
      </div>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-4 text-left">
          <div className="w-12 h-12 bg-green-700 rounded-xl flex items-center justify-center shadow-sm">
            <span className="text-white font-bold text-lg">
              {(employee.first_name || employee.firstName || '').charAt(0)}
              {(employee.last_name || employee.lastName || '').charAt(0)}
            </span>
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 leading-tight">{fullName}</h2>
            <p className="text-sm text-slate-500 font-medium">
              {employee.designation || employee.title || 'N/A'}
            </p>
          </div>
        </div>
      }
      size="md"
    >
      <div className="flex flex-col gap-8">
        {renderContent()}
        
        <div className="flex items-center justify-between pt-6 border-t border-slate-200">
          <Button variant="outline" onClick={onClose}>Close</Button>
          
          <div className="flex items-center gap-3">
            {hasPermission('workforce-employees-edit') && (
              <Button onClick={() => onEditNavigation(employee)}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Full Edit
              </Button>
            )}
            {hasPermission('workforce-employees-delete') && (
              <Button
                variant="danger"
                onClick={() => onDelete(employee.id, fullName)}
                loading={isDeleting}
                loadingText="Deleting..."
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default EmployeeDetailsModal
